import React, { Suspense, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { DupontLogger, useSalesforceSSO } from 'packages/ui-components';

import { setActiveChatData } from 'store/activeChatItemSlice';
import { setUserData } from 'store/userDataSlice';
import CHATBOT_APIS from 'constants/api.urls';
import { IUserDetails } from 'constants/chat.interface';
import { postRequest, setRequestHeaders } from 'services/api.service';

import ChatbotLoader from 'components/ChatbotLoader';

import 'packages/ui-styles/global.scss';
const Home = React.lazy(() => import('containers/ChatbotHome'));
const AdminPortal = React.lazy(() => import('containers/ChatbotAdminPortal'));
const Logout = React.lazy(() => import('containers/SSOLogout'));

function Chatbot() {
  const Logger = DupontLogger('Chatbot');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const REACT_APP_CALL_BACK_URL =
    window.location.hostname === 'localhost' ? process.env.REACT_APP_BASE_URL_LOCAL : window.location.origin;

  const AuthData = {
    loginUrl: process.env.REACT_APP_LOGIN_URL || '',
    clientId: process.env.REACT_APP_CONSUMER_ID || '',
    clientSecret: process.env.REACT_APP_COMSUMER_SECRET || '',
    redirectUri: REACT_APP_CALL_BACK_URL || '',
  };

  const { isAuthenticating, tokenData } = useSalesforceSSO({ ...AuthData });

  const navigateCallback = useCallback((url: string) => navigate(url), [navigate]);

  window.onbeforeunload = () => {
    let lastVisitedURL = window.location.pathname + window.location.search;
    if (lastVisitedURL === '/') lastVisitedURL = '/home';
    sessionStorage.setItem('LAST_VISITED_URL', lastVisitedURL);
  };

  const userLoginReport = async (userData: IUserDetails) => {
    const { First_Name, Last_Name, UserCity, UserRegion, UserCountry, UserEmail, UserId } = userData;
    try {
      const payload = {
        first_name: First_Name,
        last_name: Last_Name,
        email_id: UserEmail,
        city: UserCity,
        region: UserRegion,
        country: UserCountry,
        user_sso_id: UserId,
      };
      const userDetails = await postRequest(CHATBOT_APIS.reportLogin, payload);
      dispatch(setUserData({ userDetails }));
    } catch (err) {
      Logger.error('err: ', err);
    }
  };

  const readQueryParameters: any = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const appInfo = searchParams.get('appInfo');
    if (appInfo) {
      const parsedAppInfo = JSON.parse(decodeURIComponent(appInfo));
      Logger.log('Query Params: ', parsedAppInfo);
    }
  };

  useEffect(() => {
    readQueryParameters();
    const activeChatId = sessionStorage.getItem('activeChatId');
    const chat_history_id = activeChatId ? (parseInt(activeChatId) >= 0 ? activeChatId : null) : null;
    const previousChatData = {
      isLoading: Boolean(chat_history_id),
      chat_history_id,
      history: [],
    };
    dispatch(setActiveChatData(previousChatData));
  }, []);

  useEffect(() => {
    if (!isAuthenticating && tokenData) {
      dispatch(setUserData(tokenData));
      setRequestHeaders(tokenData.authToken, tokenData.userDetails.UserEmail);
      const lastVisitedURL = sessionStorage.getItem('LAST_VISITED_URL') || '/home';
      Logger.log('lastVisitedURL: ', lastVisitedURL);
      navigateCallback(lastVisitedURL);
      userLoginReport(tokenData.userDetails);
    }
  }, [tokenData]);

  return (
    <>
      {isAuthenticating ? (
        <ChatbotLoader />
      ) : (
        <Suspense fallback={<ChatbotLoader />}>
          <Routes>
            <Route path='/home' element={<Home />} />
          </Routes>
          <Routes>
            <Route path='/admin-portal' element={<AdminPortal />} />
          </Routes>
          <Routes>
            <Route path='/logout' element={<Logout />} />
          </Routes>
        </Suspense>
      )}
    </>
  );
}

export default Chatbot;
